import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Footer from '../../components/ui/Footer';
import Logo from '../../components/ui/Logo';
import RegistrationForm from './components/RegistrationForm';
import SubscriptionTiers from './components/SubscriptionTiers';
import TrustSignals from './components/TrustSignals';
import ProgressIndicator from './components/ProgressIndicator';
import PaymentForm from './components/PaymentForm';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';
import { sendConfirmationEmail } from '../../services/emailService';

const Register = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedTier, setSelectedTier] = useState('professional');
  const [registrationData, setRegistrationData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const toast = useToast();

  const steps = [
    { id: 'account', title: 'Account Details', description: 'Basic information', icon: 'User' },
    { id: 'subscription', title: 'Choose Plan', description: 'Select subscription', icon: 'CreditCard' },
    { id: 'payment', title: 'Payment', description: 'Billing information', icon: 'Lock' }
  ];

  const handleRegistrationSubmit = async (formData) => {
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const result = await signUp(
        formData?.email,
        formData?.password,
        {
          first_name: formData?.firstName,
          last_name: formData?.lastName,
          full_name: `${formData?.firstName} ${formData?.lastName}`,
          phone: formData?.phone,
          job_title: formData?.jobTitle,
          company_name: formData?.companyName,
          businessName: formData?.companyName,
          business_type: formData?.businessType,
          industry: formData?.industry,
          website: formData?.website || null,
          terms_of_service_accepted: formData?.termsAccepted,
          privacy_policy_accepted: formData?.privacyAccepted,
          terms_accepted_at: new Date()?.toISOString()
        }
      );

      if (!result?.success) {
        const errMsg = result?.error || 'Registration failed. Please try again.';
        if (errMsg?.includes('User already registered') || errMsg?.includes('already exists') || errMsg?.includes('already in use')) {
          setError('An account with this email already exists. Please sign in instead.');
        } else if (errMsg?.includes('Invalid email')) {
          setError('Please enter a valid email address.');
        } else if (errMsg?.includes('Password should be at least')) {
          setError('Password must be at least 8 characters long.');
        } else if (errMsg?.includes('Unable to connect') || errMsg?.includes('network')) {
          setError('Network error. Please check your connection and try again.');
        } else {
          setError(errMsg);
        }
        toast?.error(errMsg, 'Registration Failed');
        return;
      }

      // Store registration data
      setRegistrationData({ ...formData, userId: result?.user?.id });

      // Check if email confirmation is needed
      if (result?.needsConfirmation || !result?.session) {
        // Send branded supplementary confirmation email via edge function (fire and forget)
        sendConfirmationEmail({
          to: formData?.email,
          userName: formData?.firstName,
          confirmationUrl: `https://leadgenpr6367.builtwithrocket.new/email-confirmation?pending=true&email=${encodeURIComponent(formData?.email)}`,
        })?.catch(() => {});

        toast?.success('Account created! Please check your email to confirm your account.', 'Registration Successful');
        // Redirect to email confirmation page with email pre-filled
        navigate(`/email-confirmation?pending=true&email=${encodeURIComponent(formData?.email)}`);
        return;
      }

      toast?.success('Account created successfully! Choose your plan.', 'Welcome to LeadGen Pro!');
      // User is immediately signed in - move to plan selection
      setCurrentStep(1);
    } catch (err) {
      console.error('Registration error:', err);
      const errMsg = err?.message || 'Registration failed. Please try again.';
      setError(errMsg);
      toast?.error(errMsg, 'Registration Failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTierSelect = (tierId) => {
    setSelectedTier(tierId);
  };

  const handleSubscriptionNext = () => {
    if (selectedTier === 'basic') {
      handleCompleteRegistration();
    } else {
      setCurrentStep(2);
    }
  };

  const handlePaymentSubmit = async (paymentData) => {
    setIsLoading(true);
    setError('');

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      handleCompleteRegistration();
    } catch (err) {
      console.error('Payment error:', err);
      setError('Payment processing failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteRegistration = () => {
    navigate('/dashboard', {
      state: {
        message: 'Welcome to LeadGen Pro! Your account has been created successfully.',
        type: 'success'
      }
    });
  };

  const handleStepBack = () => {
    if (currentStep > 0) {
      setError('');
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-foreground mb-2">Create Your Account</h2>
              <p className="text-muted-foreground">Join thousands of businesses growing with LeadGen Pro</p>
            </div>

            {/* Success Message (email confirmation needed) */}
            {successMessage && (
              <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Icon name="Mail" size={18} className="flex-shrink-0 mt-0.5 text-blue-600" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900 mb-1">Check Your Email</p>
                    <p className="text-sm text-blue-800 leading-relaxed">{successMessage}</p>
                    <div className="mt-3">
                      <Link
                        to="/login"
                        className="inline-flex items-center text-sm font-medium text-blue-700 hover:text-blue-800 underline"
                      >
                        Go to Sign In →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && !successMessage && (
              <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertCircle" size={18} className="flex-shrink-0 mt-0.5 text-red-600" />
                  <div className="flex-1">
                    <p className="text-sm text-red-800 leading-relaxed">{error}</p>
                    {error?.includes('already exists') && (
                      <div className="mt-2">
                        <Link
                          to="/login"
                          className="text-sm font-medium text-red-700 hover:text-red-800 underline"
                        >
                          Sign in instead →
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {!successMessage && (
              <RegistrationForm
                onSubmit={handleRegistrationSubmit}
                isLoadingProp={isLoading}
              />
            )}
          </div>
        );

      case 1:
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-foreground mb-2">Choose Your Plan</h2>
              <p className="text-muted-foreground">Select the perfect plan for your business needs</p>
            </div>
            <SubscriptionTiers
              selectedTier={selectedTier}
              onTierSelect={handleTierSelect}
            />
            <div className="flex justify-center space-x-4">
              <Button
                variant="outline"
                onClick={handleStepBack}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back
              </Button>
              <Button
                variant="default"
                onClick={handleSubscriptionNext}
                iconName="ArrowRight"
                iconPosition="right"
                loading={isLoading}
              >
                {selectedTier === 'basic' ? 'Complete Registration' : 'Continue to Payment'}
              </Button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-foreground mb-2">Payment Information</h2>
              <p className="text-muted-foreground">
                Secure payment to activate your {selectedTier} plan
              </p>
            </div>

            {error && (
              <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertCircle" size={18} className="text-red-600 flex-shrink-0" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}

            <PaymentForm
              selectedTier={selectedTier}
              billingCycle="monthly"
              onSubmit={handlePaymentSubmit}
              isLoading={isLoading}
            />
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={handleStepBack}
                iconName="ArrowLeft"
                iconPosition="left"
                disabled={isLoading}
              >
                Back to Plans
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Logo
              size="small"
              showText={true}
              linkTo="/"
              variant="header"
            />
            <div className="flex items-center space-x-4">
              <span className="text-sm text-muted-foreground">Already have an account?</span>
              <Link to="/login">
                <Button variant="outline" size="sm">
                  Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Registration Flow */}
          <div className="lg:col-span-2">
            <div className="bg-card rounded-lg border border-border p-6 lg:p-8 shadow-elevation-1">
              {/* Progress Indicator */}
              <div className="mb-8">
                <ProgressIndicator
                  currentStep={currentStep}
                  totalSteps={steps?.length}
                  steps={steps}
                />
              </div>

              {/* Step Content */}
              {renderStepContent()}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              {/* Trust Signals */}
              <div className="bg-card rounded-lg border border-border p-6 shadow-elevation-1">
                <TrustSignals />
              </div>

              {/* Help Section */}
              <div className="bg-card rounded-lg border border-border p-6 shadow-elevation-1">
                <div className="text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon name="HelpCircle" size={24} className="text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Need Help?</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Our support team is here to help you get started
                  </p>
                  <div className="space-y-3">
                    <button className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 shadow-md transition-all duration-200 hover:shadow-lg rounded-md p-3 flex flex-col items-center justify-center space-y-1">
                      <Icon name="MessageCircle" size={18} className="text-white" />
                      <span className="text-sm font-medium">Live Chat</span>
                    </button>
                    <button className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 text-white hover:from-blue-600 hover:to-cyan-700 shadow-md transition-all duration-200 hover:shadow-lg rounded-md p-3 flex flex-col items-center justify-center space-y-1">
                      <Icon name="Phone" size={18} className="text-white" />
                      <span className="text-sm font-medium">Call Support</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="text-center text-sm text-muted-foreground">
                <p>Questions? Contact us at</p>
                <p className="font-medium text-foreground">support@leadgenproai.us</p>
                <p>or call (844) 978-1051</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Register;